import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";
import { literal, nativeEnum, object } from "zod/v4";
import { ActionableContentSchema } from "../concierge-conversation/actionable-content";
import {
  ComponentConfirmationState,
  ContentType,
  IntentType,
} from "../conversation";
import { BaseMessageDbParser } from "./base-conversation-message-db-parser";

/**
 * Utility type that removes the _id field from a type.
 * This is commonly used for message types that need to be created without an ID.
 */
export type WithoutId<T> = Omit<T, "_id">;

export const ComponentStateInfoSchema = z.object({
  confirmationState: createEnum(ComponentConfirmationState, {
    enumName: "ComponentConfirmationState",
  }).optional(),
  toolResult: z
    .object({ output: z.string().optional(), isError: z.boolean().optional() })
    .optional(),
});

// Common base schema for all component messages
export const BaseToolCallMessageSchema = BaseMessageDbParser.extend({
  confirmationStateInformation: ComponentStateInfoSchema.optional().default({
    confirmationState: ComponentConfirmationState.PENDING,
  }),
});

// Auto-generated fields schema
export const AutoGeneratedFieldsSchema = object({ _id: zExt.objectId() });

// Base schema for component messages with actionable content
export const BaseActionableComponentMessageSchema =
  BaseToolCallMessageSchema.extend({
    intent: nativeEnum(IntentType).optional(),
    actionableContent: ActionableContentSchema.optional(),
  });

export const BaseActionableContentMessageSchema = BaseMessageDbParser.extend({
  intent: nativeEnum(IntentType).optional(),
  actionableContent: ActionableContentSchema.optional(),
});

export const EmailInputActionableContentSchema = z.object({
  onSubmit: z.object({ type: z.literal("set_email") }),
});
/**
 * Message type for customer email entry.
 */
export const EmailInputMessageSchema =
  BaseActionableComponentMessageSchema.extend({
    contentType: literal(ContentType.EMAIL_INPUT),
    structuredContent: object({
      customerEmail: z.null(),
      customerInputContent: z.literal(true),
    }).optional(),
    actionableContent: EmailInputActionableContentSchema,
  });

export type EmailInputMessageType = z.infer<typeof EmailInputMessageSchema>;
export const EmailInputMessageDbParser = EmailInputMessageSchema.merge(
  AutoGeneratedFieldsSchema,
);

// Type helper to extract messages with component state
export type HasComponentState<T> = T extends {
  confirmationStateInformation?: any;
}
  ? T
  : never;

// Base types for messages with component state
export type BaseToolCallMessage = z.infer<typeof BaseToolCallMessageSchema>;
export type BaseActionableComponentMessage = z.infer<
  typeof BaseActionableComponentMessageSchema
>;

// Type helper to extract messages with customerEmail in structuredContent
export type HasCustomerEmail<T> = T extends {
  structuredContent?: { customerEmail?: any };
}
  ? T
  : never;
